import { useEffect, useState } from 'react';
import { api, describeError, type RoutinesView } from './api.js';

const STATE_LABEL = { up: '🟢 Up', down: '🔴 Down', unknown: '⚪ Checking' } as const;

/** What Alfred does on a schedule, and which sites he watches. Set up by asking him in chat. */
export function RoutinesSettings() {
  const [data, setData] = useState<RoutinesView | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const refresh = () =>
    api
      .routines()
      .then(setData)
      .catch(() => {});
  useEffect(() => void refresh(), []);

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setMessage(null);
    try {
      await fn();
      await refresh();
    } catch (err) {
      setMessage(describeError(err));
    } finally {
      setBusy(false);
    }
  };

  if (!data) return null;
  return (
    <section>
      <h3>Scheduled &amp; monitored</h3>
      <p className="muted small">
        Ask Alfred in chat, e.g. “every weekday at 9 tell me what in my inbox needs me”, “remind me
        at 3pm to call Mum”, or “tell me if alfredconsole.com goes down”.
      </p>
      {data.schedules.length === 0 && data.monitors.length === 0 && (
        <p className="small">Nothing set up yet.</p>
      )}
      <ul className="routine-list">
        {data.schedules.map((s) => (
          <li key={s.id}>
            <div>
              <strong>
                {s.kind === 'reminder' ? '⏰ ' : ''}
                {s.title}
              </strong>
              <div className="muted small">
                {s.when}
                {s.status === 'paused' ? ' · paused' : ''}
                {s.lastError ? ` · last run: ${s.lastError}` : ''}
              </div>
            </div>
            <button
              className="btn btn-small"
              disabled={busy}
              onClick={() => void run(() => api.cancelSchedule(s.id))}
            >
              Cancel
            </button>
          </li>
        ))}
        {data.monitors.map((m) => (
          <li key={m.id}>
            <div>
              <strong>{m.name}</strong>
              <div className="muted small">
                {STATE_LABEL[m.state]}
                {m.lastError && m.state !== 'up' ? ` · ${m.lastError}` : ''}
              </div>
            </div>
            <button
              className="btn btn-small"
              disabled={busy}
              onClick={() => void run(() => api.removeMonitor(m.id))}
            >
              Remove
            </button>
          </li>
        ))}
      </ul>
      {message && <p className="small">{message}</p>}
    </section>
  );
}
